import {
    isLicenseOperationContextFieldOptional,
    licenseOperationContextFields,
    licenseOperationContextShape,
    LicenseRestrictedOperation,
    LicenseRestrictedOperationContext,
} from '../models/license-restricted-operation.model';
import { LicenseUpgradeRequestTarget } from '../services/license-upgrade-display.abstract-service';

const fallbackTarget: LicenseUpgradeRequestTarget = {
    operation: 'UNKNOWN_OPERATION',
};

function isKnownOperation(value: unknown): value is LicenseRestrictedOperation {
    return (
        typeof value === 'string' &&
        Object.hasOwn(licenseOperationContextShape, value)
    );
}

/**
 * Reads the refused operation and its references from the body of a licence 403,
 * which uses the same field names as the upgrade request.
 *
 * Anything that could not be filed as-is collapses onto `UNKNOWN_OPERATION`: an
 * unrecognised code, or a known one missing a reference it requires, since the
 * backend refuses a partial context with a 400. An optional reference may be
 * missing or `null`.
 */
export function licenseUpgradeTargetFromErrorBody(
    body: unknown,
): LicenseUpgradeRequestTarget {
    const data = (body ?? {}) as Record<string, unknown>;

    const { operation } = data;
    if (!isKnownOperation(operation)) return fallbackTarget;

    const context: LicenseRestrictedOperationContext = {};
    for (const field of licenseOperationContextFields(operation)) {
        const value = data[field];
        if (
            value == null &&
            isLicenseOperationContextFieldOptional(operation, field)
        ) {
            continue;
        }
        if (typeof value !== 'number' || !Number.isFinite(value)) {
            return fallbackTarget;
        }
        context[field] = value;
    }

    return { operation, context };
}
